import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSEmploymentData,
  type EnrollmentHYCSEmploymentSubmitInput,
  type EnrollmentHYCSEmploymentSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS Employment data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSEmploymentData>
 * @example const response = await getEnrollmentHYCSEmploymentData()
 */
export const getEnrollmentHYCSEmploymentData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSEmploymentData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSEmploymentData',
      query: `mutation getEnrollmentHYCSEmploymentData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSEmploymentData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSEmploymentData = createQueryHook<undefined, EnrollmentHYCSEmploymentData>(
  getEnrollmentHYCSEmploymentData,
)

/**
 * Submit Enrollment HYCS Employment data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSEmploymentSubmitOutput>
 * @example const response = await submitEnrollmentHYCSEmploymentData();
 */
export const submitEnrollmentHYCSEmploymentData = async (
  enrollmentEmploymentSubmitInput: EnrollmentHYCSEmploymentSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSEmploymentSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSEmploymentData',
      query: `mutation submitEnrollmentHYCSEmploymentData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentEmploymentSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSEmploymentSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSEmploymentData = createMutationHook<
  EnrollmentHYCSEmploymentSubmitInput,
  EnrollmentHYCSEmploymentSubmitOutput
>(submitEnrollmentHYCSEmploymentData)
